import { useState, useEffect, useRef } from "react";
import { Search, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DatePresets from "@/components/ui/date-presets";
import DocTypeMultiSelect, {
  FALLBACK_GROUP_MAP,
} from "@/components/documents/DocTypeMultiSelect";

export default function DocumentFilters({
  filters,
  onFilterChange,
  portals,
  members,
  docTypes,
  docTypeGroups,
  groups,
  // Multi-select doc type props (URL-driven, lifted to parent)
  selectedDocTypes,
  onDocTypesChange,
}) {
  // When the parent passes a non-empty docTypes array, use it (portal-scoped).
  // Falls back to the canonical list for back-compat.
  const docTypeList =
    Array.isArray(docTypes) && docTypes.length > 0
      ? docTypes.map((t) => (typeof t === "string" ? { id: t, name: t } : t))
      : FALLBACK_GROUP_MAP.flatMap(({ types }) =>
          types.map((t) => ({ id: t, name: t }))
        );

  const [searchInput, setSearchInput] = useState(filters.search || "");
  const debounceRef = useRef(null);

  // Sync local search input with external filter reset
  useEffect(() => {
    setSearchInput(filters.search || "");
  }, [filters.search]);

  // Debounced search: 300ms
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onFilterChange({ ...filters, search: searchInput });
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const handlePortalChange = (value) => {
    onFilterChange({ ...filters, portal: value === "__all__" ? "" : value });
  };

  const handleMemberChange = (value) => {
    onFilterChange({ ...filters, memberName: value === "__all__" ? "" : value });
  };

  const handleGroupChange = (value) => {
    onFilterChange({ ...filters, groupId: value === "__all__" ? "" : value });
  };

  const handleDateFromChange = (e) => {
    onFilterChange({ ...filters, dateFrom: e.target.value });
  };

  const handleDateToChange = (e) => {
    onFilterChange({ ...filters, dateTo: e.target.value });
  };

  const handleClear = () => {
    setSearchInput("");
    onFilterChange({ portal: "", memberName: "", groupId: "", dateFrom: "", dateTo: "", search: "" });
    if (onDocTypesChange) onDocTypesChange([]);
  };

  const safeSelected = Array.isArray(selectedDocTypes) ? selectedDocTypes : [];

  const hasActiveFilters =
    filters.portal ||
    filters.memberName ||
    filters.groupId ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.search ||
    safeSelected.length > 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap gap-3 items-end">
        {/* Portal filter */}
        <div className="min-w-[140px]">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            Portal
          </p>
          <Select
            value={filters.portal || "__all__"}
            onValueChange={handlePortalChange}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All Portals" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Portals</SelectItem>
              {(portals || []).map((p) => (
                <SelectItem key={p.id || p} value={p.id || p}>
                  {p.name || p.label || p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Member filter */}
        <div className="min-w-[140px]">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            Member
          </p>
          <Select
            value={filters.memberName || "__all__"}
            onValueChange={handleMemberChange}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="All Members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Members</SelectItem>
              <SelectItem value="__none__">(No Member)</SelectItem>
              {(members || []).map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Group filter */}
        {groups && groups.length > 0 && (
          <div className="min-w-[140px]">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
              Group
            </p>
            <Select
              value={filters.groupId || "__all__"}
              onValueChange={handleGroupChange}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Groups</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.group_id} value={g.group_id}>
                    {g.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Doc Type multi-select */}
        <div className="min-w-[160px]">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            Doc Type
          </p>
          <DocTypeMultiSelect
            selected={safeSelected}
            onChange={onDocTypesChange || (() => {})}
            docTypeOptions={docTypeList}
            docTypeGroups={docTypeGroups}
          />
        </div>

        {/* Date From */}
        <div>
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            From
          </p>
          <Input
            type="date"
            value={filters.dateFrom}
            onChange={handleDateFromChange}
            className="h-8 text-xs w-[140px]"
          />
        </div>

        {/* Date To */}
        <div>
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            To
          </p>
          <Input
            type="date"
            value={filters.dateTo}
            onChange={handleDateToChange}
            className="h-8 text-xs w-[140px]"
          />
        </div>

        <DatePresets
          from={filters.dateFrom}
          to={filters.dateTo}
          onChange={(from, to) => onFilterChange({ ...filters, dateFrom: from, dateTo: to })}
          className="pb-0.5"
        />

        {/* Search */}
        <div className="flex-1 min-w-[180px]">
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">
            Search
          </p>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search documents..."
              className="h-8 text-xs pl-8"
            />
          </div>
        </div>

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClear}
            className="h-8 text-xs gap-1"
            data-testid="clear-all-filters"
          >
            <X className="w-3 h-3" />
            Clear
          </Button>
        )}
      </div>

      {/* Selected doc-type chips row */}
      {safeSelected.length > 0 && (
        <div
          className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100 items-center"
          data-testid="doc-type-chips"
        >
          {safeSelected.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 text-gray-700 text-xs px-2 py-0.5"
            >
              {t}
              <button
                type="button"
                aria-label={`Remove ${t}`}
                onClick={() =>
                  onDocTypesChange && onDocTypesChange(safeSelected.filter((v) => v !== t))
                }
                className="hover:text-gray-900 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            data-testid="doc-type-clear-all"
            onClick={() => onDocTypesChange && onDocTypesChange([])}
            className="text-xs text-gray-500 underline hover:text-gray-700 ml-1"
          >
            Clear all
          </button>
        </div>
      )}
    </Card>
  );
}
